import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { LANGS, useI18n } from './index';
import './LanguageSwitcher.css';

/**
 * F1 — בורר השפה. יושב בהדר, ליד כפתור-ההתנתקות.
 *
 * ★ כל שפה מוצגת **בשפת עצמה** (‏`nativeLabel`) ולא בעברית: אזרח שאינו קורא
 *   עברית לא יכול למצוא "ערבית" ברשימה — הוא מחפש "العربية". זו הסיבה שכל
 *   בוררי-השפה ברשת בנויים כך.
 * ★ קוד-השפה מוצג לצד התווית (‏AR / RU) כעוגן ויזואלי מהיר.
 */
export default function LanguageSwitcher({ className = '' }) {
  const { lang, langs, setLang, t } = useI18n();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  /* ★★ מוצגות **רק** השפות שהלקוח הדליק בהגדרות-האתר, ועברית תמיד ראשונה.
     ⚠️ לקוח שלא הדליק אף שפה ⇒ הרכיב אינו מרנדר כלום. בלי זה כל 15 הלקוחות
        הקיימים היו מקבלים פתאום בורר-שפה שאיש לא ביקש. */
  const visible = LANGS.filter((l) => l.code === 'he' || langs.indexOf(l.code) !== -1);
  const current = visible.find((l) => l.code === lang) || visible[0];

  // רק עברית פעילה ⇒ אין מה לבחור
  if (visible.length < 2) { return null; }

  const choose = (code) => {
    setLang(code);
    setAnchorEl(null);
  };

  return (
    <>
      <button
        type="button"
        className={('ans-lang-btn ' + className).trim()}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t('lang.chooseLanguage')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <LanguageRoundedIcon className="ans-lang-icon" />
        <span className="ans-lang-code">{current.code.toUpperCase()}</span>
        {/* ★ שברון שמסתובב בפתיחה — מסמן שהכפתור פותח תפריט ולא מבצע פעולה.
            ‏`aria-hidden`: מצב-הפתיחה כבר מוכרז דרך `aria-expanded` שעל הכפתור,
            וקורא-מסך שהיה מקריא גם את האייקון היה חוזר על אותו מידע. */}
        <KeyboardArrowDownRoundedIcon
          className={'ans-lang-chev' + (open ? ' is-open' : '')}
          aria-hidden="true"
        />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        className="ans-lang-menu"
        /* ★ העוגן נשען על `end` הלוגי ולא על ימין/שמאל פיזיים, כדי שהתפריט
           ייפתח נכון גם ב-LTR (רוסית/אנגלית) בלי כלל-CSS נפרד. */
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        MenuListProps={{ role: 'listbox', 'aria-label': t('lang.chooseLanguage') }}
      >
        {visible.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === lang}
            role="option"
            aria-selected={l.code === lang}
            onClick={() => choose(l.code)}
            className="ans-lang-item"
            /* ⚠️ כל פריט מקבל את הכיווניות **של השפה שהוא מייצג** — אחרת
               "العربية" ברשימה תחת ממשק אנגלי מרונדרת עם סימני-פיסוק
               במקום הלא-נכון. */
            dir={l.dir}
            lang={l.code}
          >
            <span className="ans-lang-item-label">{l.nativeLabel}</span>
            {l.code === lang && <CheckRoundedIcon className="ans-lang-item-check" fontSize="small" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
